import { map, Observable } from 'rxjs';
import { IUrlProviderDTO } from '../../../dto/url-provider.dto';
import { IUrlProvider } from '../../url.provider';
import { MediaService } from '../../../../../service/media.service';
import { IMediaDTO } from '../../../../../dto/media/media.dto';
import { MediaType } from '../../../../../dto/enum/media-type.enum';
import { IPictureMediaDTO } from '../../../../../dto/media/picture-media.dto';
import { IContentDTO } from '../../../../../dto/content/content.dto';
import { IVideoMediaDTO } from '../../../../../dto/media/video-media.dto';
import { ICarouselContainerMediaDTO } from '../../../../../dto/media/carousel-container-media.dto';
import { ICarouselItemMediaDTO } from '../../../../../dto/media/carousel-item-media.dto';
import { ICarouselItemPictureMediaDTO } from '../../../../../dto/media/carousel-item-picture-media.dto';
import { ICarouselItemVideoMediaDTO } from '../../../../../dto/media/carousel-item-video-media.dto';
import { DownloadType } from '../../../dto/enum/download-type.enum';
import { ILightUserDTO } from '../../../../../dto/user/light-user.dto';
import { IUrlProviderUserDTO } from '../../../dto/url-provider-user.dto';
import { inject } from '@angular/core';

export abstract class AbstractMediaUrlProvider implements IUrlProvider {
  private static readonly VALID_MEDIA_TYPES: MediaType[] = [
    MediaType.CAROUSEL,
    MediaType.PICTURE,
    MediaType.VIDEO,
  ];
  private static readonly CAPTCHA_ACTION: 'fetchMedia' = 'fetchMedia' as const;
  abstract getType: () => DownloadType;
  private readonly mediaService: MediaService = inject(MediaService);

  public getCaptchaCData = (): undefined => undefined;

  public getCaptchaAction = (): string =>
    AbstractMediaUrlProvider.CAPTCHA_ACTION;

  public getUrls(
    payload: string,
    captchaToken: string
  ): Observable<IUrlProviderDTO> {
    return this.mediaService.fetchMediaByUrl(payload, captchaToken).pipe(
      map((mediaDTO: IMediaDTO): IUrlProviderDTO => {
        if (
          !AbstractMediaUrlProvider.VALID_MEDIA_TYPES.includes(
            mediaDTO.mediaType
          )
        ) {
          return {
            user: this.ofLightUserDTO(mediaDTO.user),
            urls: [],
          };
        }
        const urls: string[] = [];
        switch (mediaDTO.mediaType) {
          case MediaType.PICTURE:
            urls.push(this.ofPictureMediaDTO(mediaDTO));
            break;
          case MediaType.VIDEO:
            urls.push(this.ofVideoMediaDTO(mediaDTO));
            break;
          case MediaType.CAROUSEL:
            urls.push(...this.ofCarouselContainerMediaDTO(mediaDTO));
        }
        return {
          user: this.ofLightUserDTO(mediaDTO.user),
          urls: urls,
        };
      })
    );
  }

  private ofPictureMediaDTO(mediaDTO: IMediaDTO): string {
    const pictureMediaDTO: IPictureMediaDTO = mediaDTO as IPictureMediaDTO;
    const imageContentDTO: IContentDTO = pictureMediaDTO.images[0];
    return imageContentDTO.url;
  }

  private ofVideoMediaDTO(mediaDTO: IMediaDTO): string {
    const videoMediaDTO: IVideoMediaDTO = mediaDTO as IVideoMediaDTO;
    const videoContentDTO: IContentDTO = videoMediaDTO.videos[0];
    return videoContentDTO.url;
  }

  private ofCarouselContainerMediaDTO(mediaDTO: IMediaDTO): string[] {
    const carouselContainerMediaDTO: ICarouselContainerMediaDTO =
      mediaDTO as ICarouselContainerMediaDTO;
    return carouselContainerMediaDTO.medias
      .filter(
        (carouselItemMediaDTO: ICarouselItemMediaDTO): boolean =>
          MediaType.PICTURE === carouselItemMediaDTO.mediaType ||
          MediaType.VIDEO === carouselItemMediaDTO.mediaType
      )
      .map((carouselItemMediaDTO: ICarouselItemMediaDTO): string =>
        MediaType.PICTURE === carouselItemMediaDTO.mediaType
          ? this.ofCarouselItemPictureMediaDTO(carouselItemMediaDTO)
          : this.ofICarouselItemVideoMediaDTO(carouselItemMediaDTO)
      );
  }

  private ofCarouselItemPictureMediaDTO(
    carouselItemMediaDTO: ICarouselItemMediaDTO
  ): string {
    const carouselItemPictureMediaDTO: ICarouselItemPictureMediaDTO =
      carouselItemMediaDTO as ICarouselItemPictureMediaDTO;
    const imageContentDTO: IContentDTO = carouselItemPictureMediaDTO.images[0];
    return imageContentDTO.url;
  }

  private ofICarouselItemVideoMediaDTO(
    carouselItemMediaDTO: ICarouselItemMediaDTO
  ): string {
    const carouselItemVideoMediaDTO: ICarouselItemVideoMediaDTO =
      carouselItemMediaDTO as ICarouselItemVideoMediaDTO;
    const videoContentDTO: IContentDTO = carouselItemVideoMediaDTO.videos[0];
    return videoContentDTO.url;
  }

  private ofLightUserDTO(lightUserDTO: ILightUserDTO): IUrlProviderUserDTO {
    return {
      primaryKey: lightUserDTO.primaryKey,
      username: lightUserDTO.username,
      fullName: lightUserDTO.fullName,
      pictureUrl: lightUserDTO.pictureHDUrl ?? lightUserDTO.pictureUrl,
      private: lightUserDTO.private,
      verified: lightUserDTO.verified,
    };
  }
}
